/**
 * ImpactSummaryCards
 * Shared impact-summary presentation used by both the Rate Change preview
 * (RateChangePreviewModal) and the schedule-amendment preview
 * (ScheduleImpactPreviewModal). Renders Current / Proposed / Difference interest
 * cards with increase=red / decrease=green colouring, a %-change chip, and a WHT
 * impact line — keeping the two previews visually consistent.
 *
 * Optionally accepts an `extraCards` node (e.g. the rollover/extend "Schedule
 * Entries" count) composed alongside the shared cards.
 */

import React from 'react';
import { Card, CardContent, Grid, Box, Typography, Chip } from '@mui/material';
import { formatCurrency as formatCurrencyBase, formatNumber } from '../../utils/formatters';

const ImpactSummaryCards = ({
  currentInterest,
  proposedInterest,
  difference,
  percentageChange,
  currentWht,
  proposedWht,
  currencyCode = 'ZAR',
  dp = 3,
  proposedLabel = 'Proposed Total Interest',
  extraCards = null,
  cardSize = 4,
}) => {
  const fmt = (value) => formatCurrencyBase(value, currencyCode, dp);

  const diff = parseFloat(difference || 0);
  const isIncrease = diff > 0;
  const diffColor = isIncrease ? 'error.main' : 'success.main';

  const formatDifference = (value, isPercentage = false) => {
    if (value == null || (isNaN(parseFloat(value)))) return '-';
    const num = parseFloat(value);
    const sign = num >= 0 ? '+' : '';
    if (isPercentage) return `${sign}${num.toFixed(2)}%`;
    return `${sign}${currencyCode} ${formatNumber(Math.abs(num), 2)}`;
  };

  const showWht = (parseFloat(currentWht || 0) > 0) || (parseFloat(proposedWht || 0) > 0);
  const whtDiff = parseFloat(proposedWht || 0) - parseFloat(currentWht || 0);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Impact Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: cardSize }}>
            <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Current Total Interest
              </Typography>
              <Typography variant="h6">{fmt(currentInterest)}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: cardSize }}>
            <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {proposedLabel}
              </Typography>
              <Typography variant="h6" color={diffColor}>
                {fmt(proposedInterest)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: cardSize }}>
            <Box sx={{
              textAlign: 'center',
              p: 1,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: isIncrease ? '#ffebee' : '#e8f5e9',
            }}>
              <Typography variant="caption" color="text.secondary">
                Difference
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="h6" color={diffColor}>
                  {formatDifference(diff)}
                </Typography>
                {percentageChange != null && (
                  <Chip
                    label={formatDifference(percentageChange, true)}
                    size="small"
                    color={isIncrease ? 'error' : 'success'}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          {extraCards}
        </Grid>

        {showWht && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              WHT Impact: {fmt(currentWht)} → {fmt(proposedWht)} ({formatDifference(whtDiff)})
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ImpactSummaryCards;
